import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { ApiError, apiErrorMessage } from '@/shared/api-client';
import { useDeleteRole, useUpdateRole, type Role } from './api';
import { PermissionPicker } from './PermissionPicker';

interface Props {
  role: Role | null;
  onOpenChange: (open: boolean) => void;
  onActioned: (message: string) => void;
}

/**
 * Detail view for a role. System roles render read-only (their permission sets
 * change only via Flyway migration); custom roles expose the edit form (name +
 * permission picker) and a delete confirmation. The backend is the authoritative
 * gate — a stray edit/delete against a system role returns 409.
 */
export function RoleDetailDialog({ role, onOpenChange, onActioned }: Props) {
  const { t } = useTranslation('common');
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();

  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [unresolvedConfirmCount, setUnresolvedConfirmCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (role) {
      setName(role.name);
      setPermissions(new Set(role.permissions));
      setConfirmingDelete(false);
      setUnresolvedConfirmCount(null);
      setError(null);
    }
  }, [role]);

  if (!role) return null;
  const readOnly = role.isSystem;

  function toggle(code: string, checked: boolean) {
    setPermissions((current) => {
      const next = new Set(current);
      if (checked) next.add(code);
      else next.delete(code);
      return next;
    });
  }

  async function submit(confirmUnresolvedVenueAccess: boolean) {
    if (!role) return;
    setError(null);
    try {
      await updateRole.mutateAsync({
        id: role.id,
        input: {
          name: name.trim(),
          permissions: [...permissions],
          confirmUnresolvedVenueAccess,
        },
      });
      setUnresolvedConfirmCount(null);
      onOpenChange(false);
      onActioned(t('roles.toast.updated'));
    } catch (caught) {
      if (
        caught instanceof ApiError &&
        caught.code === 'role_update_requires_venue_access_confirmation'
      ) {
        const count = Number(caught.details?.unresolvedAssignmentCount ?? 0);
        setUnresolvedConfirmCount(Number.isFinite(count) && count > 0 ? count : 0);
        return;
      }
      setError(apiErrorMessage(t, 'roles', caught));
    }
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    await submit(false);
  }

  async function remove() {
    if (!role) return;
    setError(null);
    try {
      await deleteRole.mutateAsync(role.id);
      onOpenChange(false);
      onActioned(t('roles.toast.deleted'));
    } catch (caught) {
      setError(apiErrorMessage(t, 'roles', caught));
      setConfirmingDelete(false);
    }
  }

  return (
    <>
      <Dialog open={role !== null} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <form onSubmit={save} className="grid gap-4">
            <DialogHeader>
              <DialogTitle>
                {readOnly ? t('roles.detail.title') : t('roles.edit.title')}
              </DialogTitle>
              <DialogDescription>
                {readOnly ? t('roles.detail.systemNotice') : t('roles.edit.description')}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-2">
              <Label htmlFor="role-name">{t('roles.fields.name')}</Label>
              <Input
                id="role-name"
                required
                value={name}
                disabled={readOnly}
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label>{t('roles.fields.permissions')}</Label>
              <PermissionPicker selected={permissions} onToggle={toggle} disabled={readOnly} />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            {!readOnly && confirmingDelete && (
              <div className="grid gap-2 rounded-md border border-destructive/50 p-3">
                <p className="text-sm">{t('roles.confirm.delete')}</p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmingDelete(false)}
                  >
                    {t('actions.cancel')}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={deleteRole.isPending}
                    onClick={remove}
                  >
                    {t('roles.actions.confirmDelete')}
                  </Button>
                </div>
              </div>
            )}

            <DialogFooter className="sm:justify-between">
              {!readOnly && !confirmingDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setConfirmingDelete(true)}
                >
                  {t('roles.actions.delete')}
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  {readOnly ? t('roles.actions.close') : t('actions.cancel')}
                </Button>
                {!readOnly && (
                  <Button type="submit" disabled={updateRole.isPending}>
                    {updateRole.isPending ? t('roles.edit.submitting') : t('roles.edit.submit')}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={unresolvedConfirmCount !== null}
        onOpenChange={(open) => {
          if (!open) setUnresolvedConfirmCount(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('roles.confirm.unresolvedTitle')}</DialogTitle>
            <DialogDescription>
              {t('roles.confirm.unresolvedDescription', { count: unresolvedConfirmCount ?? 0 })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setUnresolvedConfirmCount(null)}>
              {t('actions.cancel')}
            </Button>
            <Button type="button" disabled={updateRole.isPending} onClick={() => void submit(true)}>
              {updateRole.isPending
                ? t('roles.edit.submitting')
                : t('roles.actions.confirmUnresolved')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
